import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Shield, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  FileText,
  ExternalLink,
  Loader2
} from 'lucide-react';

const MobileCompliance = () => {
  const navigate = useNavigate();
  const { operator, api } = useMobileAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompliance();
  }, []);

  const fetchCompliance = async () => {
    try {
      const response = await api.get('/api/mobile/compliance');
      setRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance records');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (record) => {
    const status = record.expiry_status;
    const days = record.days_until_expiry;
    
    if (status === 'expired') {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Expired {days}d ago
        </Badge>
      );
    } else if (status === 'expiring_soon') {
      return (
        <Badge className="bg-amber-500/20 text-amber-500 gap-1">
          <Clock className="h-3 w-3" />
          {days} days left
        </Badge>
      );
    } else if (status === 'valid') {
      return (
        <Badge className="bg-green-500/20 text-green-500 gap-1">
          <CheckCircle className="h-3 w-3" />
          Valid
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        No Expiry
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const openDocument = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Group records by status for summary
  const statusCounts = records.reduce((acc, r) => {
    const status = r.expiry_status || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="mobile-compliance-page">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/mobile/dashboard')}
            className="text-gray-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-white font-bold text-lg">My Compliance</h1>
            <p className="text-gray-400 text-xs">Licences, tickets & certifications</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status Summary */}
        {!loading && records.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {statusCounts.expired > 0 && (
              <Card className="bg-red-900/30 border-red-500/30">
                <CardContent className="py-3 text-center">
                  <p className="text-2xl font-bold text-red-400">{statusCounts.expired}</p>
                  <p className="text-xs text-red-300">Expired</p>
                </CardContent>
              </Card>
            )}
            {statusCounts.expiring_soon > 0 && (
              <Card className="bg-amber-900/30 border-amber-500/30">
                <CardContent className="py-3 text-center">
                  <p className="text-2xl font-bold text-amber-400">{statusCounts.expiring_soon}</p>
                  <p className="text-xs text-amber-300">Expiring Soon</p>
                </CardContent>
              </Card>
            )}
            {(statusCounts.valid > 0 || statusCounts.no_expiry > 0) && (
              <Card className="bg-green-900/30 border-green-500/30">
                <CardContent className="py-3 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {(statusCounts.valid || 0) + (statusCounts.no_expiry || 0)}
                  </p>
                  <p className="text-xs text-green-300">Valid</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108] mb-4" />
            <p className="text-gray-400">Loading your compliance records...</p>
          </div>
        ) : records.length === 0 ? (
          /* Empty State */
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="py-12 text-center">
              <Shield className="h-12 w-12 mx-auto text-gray-600 mb-4" />
              <h3 className="text-white font-medium mb-2">No Compliance Records</h3>
              <p className="text-gray-400 text-sm">
                You don't have any compliance records on file yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Compliance Records List */
          <div className="space-y-3">
            {records.map((record) => (
              <Card 
                key={record.id}
                className="bg-[#2a2a2a] border-gray-700"
                data-testid={`compliance-record-${record.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-white font-medium">
                        {record.compliance_type_name}
                      </h3>
                      {record.sub_type_names && record.sub_type_names.length > 0 && (
                        <p className="text-gray-400 text-xs mt-0.5">
                          Classes: {record.sub_type_names.join(', ')}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(record)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    {record.reference_number && (
                      <div>
                        <p className="text-gray-500 text-xs">Reference</p>
                        <p className="text-gray-300 font-mono">{record.reference_number}</p>
                      </div>
                    )}
                    {record.expiry_date && (
                      <div>
                        <p className="text-gray-500 text-xs">Expiry Date</p>
                        <p className="text-gray-300">{formatDate(record.expiry_date)}</p>
                      </div>
                    )}
                    {record.issue_date && (
                      <div>
                        <p className="text-gray-500 text-xs">Issue Date</p>
                        <p className="text-gray-300">{formatDate(record.issue_date)}</p>
                      </div>
                    )}
                  </div>

                  {/* Document Link */}
                  {record.document_url && (
                    <Button
                      variant="outline"
                      className="w-full bg-[#1a1a1a] border-gray-600 text-[#ffd108] hover:bg-[#ffd108] hover:text-[#1a1a1a]"
                      onClick={() => openDocument(record.document_url)}
                      data-testid={`view-document-${record.id}`}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Document
                      <ExternalLink className="h-3 w-3 ml-2" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCompliance;
